import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { api, medvolApiUrl, vitecompanyserviceurl } from "src/constants/global-const";
import { addAuthHeader } from "src/utils/auth";
import { attachRefreshInterceptor, getIdToken, setIdTokenHeader } from "src/utils/axiosRefreshInterceptor";
import { getOrCreateRefresh } from "src/utils/refreshLock";

const instance: AxiosInstance = axios.create({
  baseURL: api,
});

const companyserviceurl: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl + "/api/v1/oms",
});

const basecompanyserviceurl: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl,
});

const companyserviceurlevent: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl,
});

const medvolInstance: AxiosInstance = axios.create({
  baseURL: medvolApiUrl,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurl.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
basecompanyserviceurl.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurlevent.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));

// Attach 403 refresh-and-retry interceptors
attachRefreshInterceptor(instance);
attachRefreshInterceptor(companyserviceurl);
attachRefreshInterceptor(basecompanyserviceurl);
attachRefreshInterceptor(companyserviceurlevent);
// Medvol API takes the raw idToken (no "Bearer"), so retry with the refreshed idToken.
attachRefreshInterceptor(medvolInstance, setIdTokenHeader);

export const fetchInvoicesData = ({
  startDate,
  endDate,
  company_code,
  division_code,
  assignee_id,
  city,
  district,
  state,
  ordering,
  fuid_no,
  fuid_status_id,
  invoice_number,
  order_no,
  counter_code,
  stockist_medvol_code,
  company_stockist_code,
  page_size,
  page_number,
  screen,
  dateType,
  entered_by,
  approved_by,
  is_escalated,
  //KAM - INST
  uploadType,
  moduleType,
  //KAM - INST
  //M10-14101
  order_mode,
  cndn_remarks_code_int,
  //M10-14101
  textract_configured,  //textract filter
  rejection_code,
  // M10-16144: backend opt-outs for counts the caller doesn't read (see backend-oms-svc
  // getStockistAttachmentAllPayload). skip_counts drops `total` + `count`; skip_tab_counts drops `count`.
  skip_counts,
  skip_tab_counts
}: any) => {
  return companyserviceurl.get(`/stockist_attachment/all`, {
    params: {
      company_code: company_code,
      division_code: division_code,
      date_from: startDate,
      date_to: endDate,
      invoice_assignment_id: assignee_id,
      city: city,
      district: district,
      state: state,
      ordering: ordering,
      fuid_no: fuid_no,
      fuid_status_id: fuid_status_id,
      invoice_number: invoice_number,
      order_no: order_no,
      counter_code: counter_code,
      stockist_medvol_code: stockist_medvol_code,
      company_stockist_code: company_stockist_code,
      page_size: page_size,
      page: page_number,
      screen: screen,
      date_type: dateType,
      entered_by: entered_by,
      approved_by: approved_by,
      is_escalated: is_escalated,
      //KAM - INST
      uploadType: uploadType,
      moduleType: moduleType,
      //KAM - INST
      //M10-14101
      order_mode: order_mode,
      cndn_remarks_code_int: cndn_remarks_code_int,
      //M10-14101
      textract_configured: textract_configured,  //textract filter
      rejection_code: rejection_code,
      skip_counts: skip_counts, // M10-16144
      skip_tab_counts: skip_tab_counts, // M10-16144
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const fetchPriorityInvoicesData = ({
  startDate,
  endDate,
  company_code,
  division_code,
  assignee_id,
  city,
  district,
  state,
  ordering,
  fuid_no,
  fuid_status_id,
  invoice_number,
  order_no,
  counter_code,
  stockist_medvol_code,
  company_stockist_code,
  page_size,
  page_number,
  screen,
  dateType,
  entered_by,
  approved_by,
  uploadType,
  moduleType,
  //M10-14101
  order_mode,
  cndn_remarks_code_int,
  //M10-14101
  textract_configured,  //textract filter
  skip_counts // M10-16144
}: any) => {
  return companyserviceurl.get(`/stockist_attachment/all?is_prioritised=true`, {
    params: {
      company_code: company_code,
      division_code: division_code,
      date_from: startDate,
      date_to: endDate,
      invoice_assignment_id: assignee_id,
      city: city,
      district: district,
      state: state,
      ordering: ordering,
      fuid_no: fuid_no,
      fuid_status_id: fuid_status_id,
      invoice_number: invoice_number,
      order_no: order_no,
      counter_code: counter_code,
      stockist_medvol_code: stockist_medvol_code,
      company_stockist_code: company_stockist_code,
      page_size: page_size,
      page: page_number,
      screen: screen,
      date_type: dateType,
      entered_by: entered_by,
      approved_by: approved_by,
      uploadType: uploadType, //KAM - INST
      moduleType: moduleType, //KAM - INST
      //M10-14101
      order_mode: order_mode,
      cndn_remarks_code_int: cndn_remarks_code_int,
      //M10-14101
      textract_configured: textract_configured,  //textract filter
      // M10-16144: both callers (Entry + Approval Priority tabs) read only `total`, never `count`
      skip_tab_counts: true,
      skip_counts: skip_counts,
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const fetchEscalatedInvoicesData = ({
  startDate,
  endDate,
  company_code,
  division_code,
  assignee_id,
  city,
  district,
  state,
  ordering,
  fuid_no,
  fuid_status_id,
  invoice_number,
  order_no,
  counter_code,
  stockist_medvol_code,
  company_stockist_code,
  page_size,
  page_number,
  screen,
  dateType,
  entered_by,
  approved_by,
  uploadType, //KAM - INST
  moduleType, //KAM - INST
  //M10-14101
  order_mode,
  cndn_remarks_code_int,
  //M10-14101
  textract_configured,  //textract filter
  skip_counts // M10-16144
}: any) => {
  return companyserviceurl.get(`/stockist_attachment/all?is_escalated=true`, {
    params: {
      company_code: company_code,
      division_code: division_code,
      date_from: startDate,
      date_to: endDate,
      invoice_assignment_id: assignee_id,
      city: city,
      district: district,
      state: state,
      ordering: ordering,
      fuid_no: fuid_no,
      fuid_status_id: fuid_status_id,
      invoice_number: invoice_number,
      order_no: order_no,
      counter_code: counter_code,
      stockist_medvol_code: stockist_medvol_code,
      company_stockist_code: company_stockist_code,
      page_size: page_size,
      page: page_number,
      screen: screen,
      date_type: dateType,
      entered_by: entered_by,
      approved_by: approved_by,
      uploadType: uploadType,
      moduleType: moduleType,
      //M10-14101
      order_mode: order_mode,
      cndn_remarks_code_int: cndn_remarks_code_int,
      //M10-14101
      textract_configured: textract_configured,
      // M10-16144: both callers (Entry + Approval Escalated tabs) read only `total`, never `count`
      skip_tab_counts: true,
      skip_counts: skip_counts,
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getInvoiceDetails = (stockist_invoice_attachment_id: string, products: boolean, company_code: number | string | undefined) => {
  return companyserviceurl.get(
    `/stockist_attachment/details?stockist_invoice_attachment_id=${stockist_invoice_attachment_id}&invoice_id=&products=${products}`,
    {
      headers: {
        company_code: company_code || 0,
      },
    }
  );
};

// M10-15394
// Category 3 stockist e-invoice bulk-file upload listing.
export const fetchCategory3Files = (
  { medvol_code, from_date, to_date }: { medvol_code: string; from_date: string; to_date: string },
  company_code?: string | number
) => {
  return companyserviceurl.get(`/stockist_attachment/cat3-list`, {
    params: {
      medvol_code,
      from_date,
      to_date,
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};
// M10-15394

// Stockist product mapping listing (backend-oms-svc: GET /invoice/stockist-product-map).
export const getStockistProductMapList = (payload: any) => {
  return companyserviceurl.get(`/invoice/stockist-product-map`, {
    params: {
      page: payload?.page,
      page_size: payload?.page_size,
      stockist_id: payload?.stockist_id,
      stockist_code: payload?.stockist_code,
      item_code: payload?.item_code,
      ucode: payload?.ucode,
      created_by: payload?.created_by,
      search: payload?.search,
      date_from: payload?.date_from,
      date_to: payload?.date_to,
    },
    headers: {
      company_code: payload?.company_code ?? 0,
    },
  });
};

export const downloadInvoice = (file_path: string, company_code: string | number) => {
  return companyserviceurl.get(`/stockist_attachment/get-file?file_key=${file_path}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const uploadInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/invoice/add`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

//Not using
export const updateInvoice = (payload: any) => {
  return companyserviceurlevent.post(`event/invoice/product-add`, payload);
};

// REJECTION-REVIEW-WORKFLOW: list of invoices in pending-rejection-review state,
// scoped to the logged-in reviewer on the backend (via x-my-key → invoice_assignments).
export const fetchFuidRejectionInvoicesData = ({
  startDate,
  endDate,
  company_code,
  division_code,
  assignee_id,
  city,
  district,
  state,
  ordering,
  fuid_no,
  fuid_status_id,
  invoice_number,
  order_no,
  counter_code,
  stockist_medvol_code,
  company_stockist_code,
  page_size,
  page_number,
  screen,
  dateType,
  entered_by,
  approved_by,
  uploadType,
  moduleType,
  order_mode,
  cndn_remarks_code_int,
  textract_configured,
  rejection_code,
  skip_counts, // M10-16144
}: any) => {
  return companyserviceurl.get(`/stockist_attachment/all`, {
    params: {
      skip_counts, // M10-16144 (page 1 still reads count.count_kam_feedback_pending, so no skip_tab_counts)
      is_rejection_pending: true,
      is_my_rejection_review: true,
      company_code,
      division_code,
      date_from: startDate,
      date_to: endDate,
      invoice_assignment_id: assignee_id,
      city,
      district,
      state,
      rejection_code,
      ordering,
      fuid_no,
      fuid_status_id,
      invoice_number,
      order_no,
      counter_code,
      stockist_medvol_code,
      company_stockist_code,
      page_size,
      page: page_number,
      screen,
      date_type: dateType,
      entered_by,
      approved_by,
      uploadType,
      moduleType,
      order_mode,
      cndn_remarks_code_int,
      textract_configured,
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

// REJECTION-REVIEW-WORKFLOW: single reviewer endpoint.
// action='ACCEPT' → invoice transitions to REJECTED.
// action='REJECT' → pending state cleared; original user resumes entry.
export const rejectionReviewAction = (
  payload: {
    stockist_invoice_attachment_id: number;
    action: 'ACCEPT' | 'REJECT';
    reference_number?: string;
    review_remarks?: string;
  },
  company_code: string | number,
) => {
  return companyserviceurlevent.post(
    `/event/stockist_attachment/rejection-review-action`,
    {
      data: payload,
      event_type: 'STOCKIST_ATTACHMENT_REJECTION_REVIEW_ACTION',
      primary_key: payload.stockist_invoice_attachment_id,
    },
    { headers: { company_code: company_code || 0 } },
  );
};

// KAM CN-ADVICE changes ========================================
// "Feedback from Ext": invoices carrying an unresolved issue raised by the stockist from the KAM
// CN Advice screen. Each row comes back with a `kam_feedback_issues[]` array of the offending
// line items. Same filter surface as the other Entry tabs.
export const fetchKamFeedbackInvoicesData = ({
  startDate,
  endDate,
  company_code,
  division_code,
  assignee_id,
  city,
  district,
  state,
  ordering,
  fuid_no,
  fuid_status_id,
  invoice_number,
  order_no,
  counter_code,
  stockist_medvol_code,
  company_stockist_code,
  page_size,
  page_number,
  screen,
  dateType,
  entered_by,
  approved_by,
  uploadType,
  moduleType,
  order_mode,
  cndn_remarks_code_int,
  textract_configured,
  skip_counts, // M10-16144
}: any) => {
  return companyserviceurl.get(`/stockist_attachment/all`, {
    params: {
      // M10-16144: the Feedback from Ext tab reads only `total`, never `count`
      skip_tab_counts: true,
      skip_counts,
      has_kam_feedback: true,
      company_code,
      division_code,
      date_from: startDate,
      date_to: endDate,
      invoice_assignment_id: assignee_id,
      city,
      district,
      state,
      ordering,
      fuid_no,
      fuid_status_id,
      invoice_number,
      order_no,
      counter_code,
      stockist_medvol_code,
      company_stockist_code,
      page_size,
      page: page_number,
      screen,
      date_type: dateType,
      entered_by,
      approved_by,
      uploadType,
      moduleType,
      order_mode,
      cndn_remarks_code_int,
      textract_configured,
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

// Close one stockist-raised issue line. Plain CRUD (not an /event/* producer) like the rest of
// the KAM CN-advice endpoints, so there is no event stream to wait on afterwards.
export const resolveKamFeedbackIssue = (
  payload: { kam_cn_advice_reason_dtl_id: number; resolution_remarks?: string },
  company_code: string | number,
) => {
  return companyserviceurl.post(`/invoice/kam_cn_advice_resolve`, payload, {
    headers: { company_code: company_code || 0 },
  });
};
// KAM CN-ADVICE changes ========================================

// MEDVOL: M10-11303 changes ========================================
export const escalatedCompany = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/stockist_attachment/escalated-company`, payload, {
    headers: {
      company_code: company_code || 0
    },
  });
};
// MEDVOL: M10-11303 changes ========================================

export const getRejectionCategory = (company_code: string | number) => {
  return companyserviceurlevent.get(`api/v1/oms/stockist_attachment/reject-categories`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

// REJECTION-RESTRUCTURE: preflight check before confirming rejection
export const getRejectPreflight = (
  params: { stockist_invoice_attachment_id: number; rejection_code: string; mandatory_inputs?: string },
  company_code: string | number,
) => {
  return companyserviceurl.get('/stockist_attachment/reject-preflight', {
    params,
    headers: { company_code: company_code || 0 },
  });
};

// REJECTION-RESTRUCTURE: confirm rejection (event-based)
export const submitRejectInvoice = (
  payload: {
    event_type: 'STOCKIST_ATTACHMENT_REJECT';
    primary_key: number,
    data: {
      stockist_invoice_attachment_id: number;
      rejection_code: string;
      mandatory_inputs: Record<string, any>;
    };
  },
  company_code: string | number,
) => {
  return companyserviceurlevent.post('/event/stockist_attachment/reject', payload, {
    headers: { company_code: company_code || 0 },
  });
};

// REJECTION-REVOKE: revoke a rejection on a stockist attachment.
// Captures a reference number from the user and forwards it as revoke_remarks.
export const submitRejectionRevokeAction = (
  payload: {
    stockist_invoice_attachment_id: number;
    revoke_remarks?: string;
  },
  company_code: string | number,
) => {
  return companyserviceurlevent.put(
    `/event/stockist_attachment/rejection-revoke-action`,
    {
      data: payload,
      event_type: 'STOCKIST_ATTACHMENT_REJECTION_REVOKE_ACTION',
      primary_key: payload.stockist_invoice_attachment_id,
    },
    { headers: { company_code: company_code || 0 } },
  );
};

// REJECTION-RE-EDIT: minimal rejection rewrite (no master/V1/Novu side-effects).
// Same payload shape as reject; backend uses event_type to route to the rename handler.
export const submitRejectionReEditAction = (
  payload: {
    event_type: 'STOCKIST_ATTACHMENT_REJECTION_RE_EDIT_ACTION';
    primary_key: number,
    data: {
      stockist_invoice_attachment_id: number;
      rejection_code: string;
      mandatory_inputs: Record<string, any>;
    };
  },
  company_code: string | number,
) => {
  return companyserviceurlevent.put(
    '/event/stockist_attachment/rejection-re-edit-action',
    payload,
    { headers: { company_code: company_code || 0 } },
  );
};

// REJECTION-RESTRUCTURE: invoice selection list for INV-001 dropdown
export const getRejectStockistInvoices = (
  params: { stockist_invoice_attachment_id: number },
  company_code: string | number,
) => {
  return companyserviceurl.get('/stockist_attachment/reject-stockist-invoices', {
    params,
    headers: { company_code: company_code || 0 },
  });
};

// REJECTION-RESTRUCTURE: pre-fill data for mandatory input fields
export const getRejectPrefillData = (
  params: { stockist_invoice_attachment_id: number },
  company_code: string | number,
) => {
  return companyserviceurl.get('/stockist_attachment/reject-prefill-data', {
    params,
    headers: { company_code: company_code || 0 },
  });
};

// REJECTION-RESTRUCTURE: PRC-001/PRC-002 product price comparison
export const getRejectProductPrice = (
  params: { stockist_invoice_attachment_id: number; item_code: string },
  company_code: string | number,
) => {
  return companyserviceurl.get('/stockist_attachment/reject-product-price', {
    params,
    headers: { company_code: company_code || 0 },
  });
};

// M10-16200 (e-invoice round robin): optional params, e.g. { is_einvoice_reviewer: true }
// to list only the e-invoice reviewer pool. Existing callers pass none and get everyone.
export const getAssigneeList = (company_code: string | number, params?: { is_einvoice_reviewer?: boolean }) => {
  return companyserviceurl.get("/stockist_attachment/invoice-assignees", {
    params,
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-11654
export interface UpdateInvoiceAssigneeFlagsPayload {
  data: {
    invoice_assignment_id: number;
    is_round_robin_applicable?: boolean;
    is_rejection_reviewer?: boolean;
    is_einvoice_reviewer?: boolean; // M10-16200
  };
  event_type: "INVOICE_ASSIGNMENT_FLAGS_UPDATE";
  primary_key: number;
}

export const updateInvoiceAssigneeFlags = (
  payload: UpdateInvoiceAssigneeFlagsPayload,
  company_code: string | number
) => {
  return companyserviceurlevent.put(`/event/invoice_assignments/update-flags`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-11654

export const reAssignInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/stockist_attachment/reassign`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const escalateInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/stockist_attachment/escalate`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const setPriorityInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/stockist_attachment/set-priority`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const putInvoiceOnHold = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.put(`/event/stockist_attachment/hold`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//Lupin Add company code
export const fetchInvoiceStatus = (company_code: number) => {
  return companyserviceurl.get(`/stockist_attachment/fuid-status-list`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

//M10-12866
export const getSimilarCompanyProducts = (company_code: string, product_name: string, product_code: string, page: number, page_size: number,module_code?:any, allDetails?: boolean) => {
  let url = `/oms/counter/company/products?company_code=${company_code}&product_name=${product_name}&item_code=${product_code}&page=${page}&page_size=${page_size}`;
  if (module_code) {
    url += `&module_code=${module_code}`;
  }
  if (allDetails) {
    url += `&alldetails=true`;
  }
  return instance.get(url);
};
//M10-12866

export const entryInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.put(`/event/invoice/entry`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const editInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.put(`/event/invoice/edit`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
// M10-12366
export const reEditInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.put(`/event/invoice/re-entry`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
export const acceptStockistAck = (
  stockist_invoice_attachment_id: string | number,
  company_code: string | number,
  remarks: string,
) => {
  return companyserviceurlevent.put(`/event/stockist_attachment/stockist-ack`, {
    data: { stockist_invoice_attachment_id, remarks },
    event_type: "STOCKIST_ATTACHMENT_STOCKIST_ACK",
    primary_key: stockist_invoice_attachment_id,
  }, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

// M10-12366

export const getOrderedProducts = (payload: any) => {
  return companyserviceurlevent.get(
    `/api/v1/oms/order/similar-products?counter_code=${payload?.counter_code}&product_name=${payload?.product_name}&page=${payload?.page}&page_size=${payload?.page_size}&company_stockist_code=${payload?.company_stockist_code}&invoice_date=${payload?.invoice_date}&item_code=${payload?.item_code ?? ""}`, {
    headers: {
      company_code: payload?.company_code || 0,
    },
  }
  );
};

// M10-15732: PO Ref No dropdown listing for the invoice header - PO records raised for this
// counter/location in the 2 months up to the invoice date.
export const getPoRefNoListing = (payload: any) => {
  return companyserviceurl.get(`/stockist_attachment/po_ref_listing`, {
    params: {
      counter_code: payload?.counter_code,
      location_code: payload?.location_code,
      invoice_date: payload?.invoice_date,
      company_code: payload?.company_code,
    },
    headers: {
      company_code: payload?.company_code || 0,
    },
  }).then((res) => {
    // Unlike sibling endpoints on this service (which nest the list under `data`),
    // this one nests it under `result` — reshape so the shared Autocomplete's
    // `res.data.data` unwrap logic picks it up.
    res.data = { ...res.data, data: res.data?.result || [] };
    return res;
  });
};

export const getSimilarOrders = (payload: any) => {
  const tempInvoiceDateParam =
    payload?.temp_invoice_date !== undefined ? `&temp_invoice_date=${payload?.temp_invoice_date}` : ""; //M10-12020
  return companyserviceurl.get(
    `/order/similar-orders?item_code=${payload?.item_code}&inv_mrp=${payload?.inv_mrp}&counter_code=${payload?.counter_code}&company_stockist_code=${payload?.company_stockist_code}&invoice_date=${payload?.invoice_date}${tempInvoiceDateParam}&batch_no=${(payload?.batch_no ?? "").replace(/[`|]/g, "")}&company_code=${payload?.company_code}&stockist_invoice_attachment_id=${payload?.stockist_invoice_attachment_id}`, {
    headers: {
      company_code: payload?.company_code || 0,
    },
  }
  );
};

// Invoice pack change on a fixed-price order line (replace involved, pack differs). confirm:false
// returns only { plan }; confirm:true also creates / tops up the pack-change order (or reprices the
// line in place) and returns { action, order_code, stream_ids }.
export const postInvoicePackChange = (payload: any, company_code: string | number) => {
  return companyserviceurl.post(`/invoice/pack-change`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

//Not using
export const editInvoiceProduct = (payload: any) => {
  return companyserviceurlevent.post(`/event/invoice/product-edit`, payload);
};

//Not using
export const invoiceProductDelete = (payload: any) => {
  return companyserviceurlevent.delete(`/event/invoice/product-delete`, { data: payload });
};

export const getInvoiceBatchList = (itemCode: string, company_code: string | number) => {
  return companyserviceurl.get(`/invoice/product-batch?item_code=${itemCode}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getInvoicePriceList = (itemCode: string) => {
  return instance.get(`/api/v1/product/get-product-price?is_active=true&item_code=${itemCode}`);
};

export const fetchEinvoicesData = ({
  page_size,
  page_number,
  einvoice_dtl_id,
  company_code,
  division_code,
  startDate,
  endDate,
  assignee_id,
  city,
  district,
  state,
  einvoice_status,
  invoice_number,
  counter_code,
  fuid_no,
  stockist_medvol_code,
  company_stockist_code,
  attachment_type,
  is_auto_approval,
  einvoice_cat, // M10-16200
  is_my_einvoice, // M10-16200 round robin: "Yes" -> only e-invoices assigned to the caller
}: any) => {
  return companyserviceurl.get(`/stockist_attachment/e-invoices?`, {
    params: {
      page_size: page_size,
      page: page_number,
      einvoice_dtl_id: einvoice_dtl_id || null,
      company_code: company_code,
      division_code: division_code,
      date_from: startDate,
      date_to: endDate,
      einvoice_assignment_id: assignee_id, // M10-16200: reviewer the e-invoice is assigned to
      is_my_einvoice: is_my_einvoice,
      city: city,
      district: district,
      state: state,
      einvoice_status: einvoice_status,
      invoice_number: invoice_number,
      counter_code: counter_code,
      fuid_no: fuid_no,
      stockist_medvol_code: stockist_medvol_code,
      company_stockist_code: company_stockist_code,
      attachment_type: attachment_type,
      is_auto_approval:is_auto_approval,
      einvoice_cat: einvoice_cat, // M10-16200
    },
    headers: {
      company_code: company_code,
    },
  });
};

export const eInvoiceStatusUpdate = (payload: any, company_code: any) => {
  return companyserviceurlevent.put(`/event/stockist_attachment/e-invoice-status-update`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-11536
export const getInvoiceRemarks = (stockist_invoice_attachment_id: number, company_code: string | number) => {
  return companyserviceurl.get(`/stockist_attachment/remarks?stockist_invoice_attachment_id=${stockist_invoice_attachment_id}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const postInvoiceRemark = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/stockist_attachment/remark`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-11536

//Not using
export const invoiceStatusUpdate = (payload: any) => {
  return companyserviceurlevent.put(`event/stockist_attachment/status-update`, payload);
};

export const revertInvoice = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.put(`event/invoice/revert`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const multiInvoiceReassign = (payload: any, company_code: string | number) => {
  return basecompanyserviceurl.put(`multi-event/stockist_attachment/reassign`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const recallInvoice = (payload: any, company_code: string | number) => {
  return basecompanyserviceurl.delete(`event/invoice/recall`, {
    data: payload,
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const searchProduct = (payload: any) => {
  //M10-15818: KAM (module_code 4) invoices only match products linked to that module in item_division_module_lnk
  const moduleCodeParam = payload?.module_code ? `&module_code=${payload?.module_code}` : "";
  return instance.get(
    `/api/v1/product/search?search=${payload?.search}&page=${payload?.page}&perPage=${payload?.perPage}&company_code=${payload?.company_code}${moduleCodeParam}`
  );
};

export const stockistProductMap = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/invoice/stockist-product-map`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

// M10-16244: look up by stockist_invoice_attachment_id (indexed, faster); fuid is only a fallback.
// The API returns 400 when neither is sent, so never call it without one.
export const getTextractData = (
  ids: { stockist_invoice_attachment_id?: string | number; fuid?: string },
  company_code: string | number
) => {
  const params = ids.stockist_invoice_attachment_id
    ? { stockist_invoice_attachment_id: ids.stockist_invoice_attachment_id }
    : ids.fuid
      ? { fuid: ids.fuid }
      : null;
  if (!params) {
    return Promise.reject(new Error("getTextractData: stockist_invoice_attachment_id or fuid is required"));
  }
  return companyserviceurl.get(`/stockist_attachment/textract`, {
    params,
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getAnalyticsData = (company_code: string | number) => {
  return companyserviceurl.get(`/stockist_attachment/analytics`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getProductGstDetails = (item_codes: string) => {
  return instance.get(`/api/v1/product/details?item_codes=${item_codes}&fields=company_productcode%2Citem_code%2Cname%2Cgst_pc%2Cgst_ambiguous`);
};

// export const getInvoiceUsers = (keys: string, entered_by: string, approved_by: string) => {
//   return companyserviceurl.get(`/invoice/?keys=${keys}&entered_by=${entered_by}&approved_by=${approved_by}`);
// };

export const getInvoiceUsers = (query: string, company_code: string | number) => {
  return companyserviceurl.get(query, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getInvoiceActionAllowed = (invoice_id: string, stockist_invoice_attachment_id: string, action: string, company_code: string | number) => {
  return companyserviceurl.get(`/invoice/is-action-allowed?invoice_id=${invoice_id}&stockist_invoice_attachment_id=${stockist_invoice_attachment_id}&action=${action}`, {
    headers: {
      company_code: company_code || 0,
    },
  })
}

export const eInvoiceEdit = (payload: any,company_code:any) => {
  return companyserviceurlevent.put(`/event/stockist_attachment/e-invoice-edit`, payload, {
    headers: {
      company_code: company_code || 0,
    }
  });
};

export const eInvoiceExemptUpdate = (payload: any,company_code:any) => {
  return companyserviceurlevent.put(`/event/order/einvoice`, payload, {
    headers: {
      company_code: company_code || 0,
    }
  });
};

export const saoExemptUpdate = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/sao_exempt`, payload, {
    headers: {
      company_code: company_code || 0,
    }
  })
};

export const getInvoiceChangeValidationV1 = async (payload: any) => {
  // Refresh up front so the Medvol API gets a fresh idToken. Shared
  // single-flight lock, so it collapses with any in-progress refresh. On
  // failure, send anyway — the 401/403 interceptor retries and logs out if needed.
  try {
    await getOrCreateRefresh();
  } catch (error) {
    console.error("Pre-request token refresh failed:", error);
  }

  const token = getIdToken();

  if (!token) throw new Error("No token found");

  return medvolInstance.post(`/api/invoicevalidation`, payload, {
    headers: {
      Authorization: token,
    },
  });
};

export const autoInvoiceEntry = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.put(`/event/invoice/auto-invoice-entry`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  })
};

export const checkPoStatus = (payload: any, token: string) => {
  return medvolInstance.post(`/api/checkinvoice_replenishmentv2`, payload, {
    headers: {
      Authorization: getIdToken() || token,
    },
  });
};

export const escalateInvoiceToMaster = (payload: any, company_code: string | number) => {
  return companyserviceurl.post(`/stockist_attachment/master-escalation`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getMasterEscalationList = (payload: any) => {
  return companyserviceurl.get(`/stockist_attachment/master-escalation`, {
    params: {
      page: payload?.page,
      page_size: payload?.page_size,
      invoice_number: payload?.invoice_number,
      status: payload?.status,
      fuid: payload?.fuid,
      pharmacist_code: payload?.pharmacist_code,
      link_type: payload?.link_type,
      entry_type: payload?.entry_type,
      counter_code: payload?.counter_code,
      stockist_id: payload?.stockist_id,
      sort: payload?.sort,
      date: payload?.dateRange,
      company_code: payload?.company_code,
      remark_type: payload?.remark_type
    },
    headers: {
      company_code: payload?.company_code || 0,
    },
  });
};

export const closeInvoiceEscalation = (payload: any, company_code: string | number) => {
  return companyserviceurl.put(`/stockist_attachment/master-escalation`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const fetchTextractData = ({
  startDate,
  endDate,
  company_code,
  division_code,
  assignee_id,
  city,
  district,
  state,
  ordering,
  fuid_no,
  fuid_status_id,
  page_size,
  page_number,
  screen,
  dateType,
  entered_by,
  approved_by,
  stockist_medvol_code,
  textract_configured
}: any) => {
  return companyserviceurl.get(`/stockist_attachment/textract-stockist`, {
    params: {
      company_code: company_code,
      division_code: division_code,
      date_from: startDate,
      date_to: endDate,
      invoice_assignment_id: assignee_id,
      city: city,
      district: district,
      state: state,
      ordering: ordering,
      fuid_no: fuid_no,
      fuid_status_id: fuid_status_id,
      page_size: page_size,
      page: page_number,
      screen: screen,
      date_type: dateType,
      entered_by: entered_by,
      approved_by: approved_by,
      stockist_medvol_code: stockist_medvol_code,
      textract_configured: textract_configured
    },
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getRemarkHeader = () => {
  return companyserviceurl.get(`/stockist_attachment/escalation-reason`);
};
//M10-10639
export const getCheckFullyInvoiced = (invoice_id: any, company_code: any, payload: any) => {
  return companyserviceurl.post(`/invoice/check-fully-invoiced?invoice_id=${invoice_id}`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-10639
//M10-12287
export const uploadStockistFile = (params: FormData, company_code: any) => {
  return companyserviceurl.post(
    "/stockist_attachment/upload",
    params,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        company_code: company_code || 0,
      },
    }
  );
};

export const addStockistAttachmentEvent = (payload: any, company_code: any) => {
  return companyserviceurlevent.post(
    "/event/stockist_attachment",
    payload,
    {
      headers: {
        company_code: company_code || 0,
      },
    }
  );
};
export const getAttachmentDetails = (payload: any) => {
  return companyserviceurl.get(
    "/order/company-orders",
    {
      params: payload,
      headers: {
        company_code: payload?.company_code || 0,
      },
    }
  );
};
//M10-12287
//M10-12755
export const renameInvoiceNumber = (payload: any, company_code: string | number) => {
  return companyserviceurlevent.post(`/event/invoice/rename-invoice-number`, payload, {
    headers: {
      company_code: company_code || 0,
    },
  });
};
//M10-12755